/**
 * #1060 — where the Kalshi account is, for read surfaces that explain it in
 * place (the side rail, the board's positions cell).
 *
 * #1041 split "checking keys" from "no keys" (`accountStore.credentialsStatus`);
 * these surfaces used to read `status !== 'connected'` alone, so a trader whose
 * keys were still being checked, or whose check failed, was told to connect
 * Kalshi. The words and their precedence are the order ticket's reserved line
 * (`useOrderEntryModel`); `useKalshiAccountPhase` additionally holds a failed
 * connection through the store's retry attempts, where the ticket's single
 * line reads "Connecting to Kalshi…" during each attempt; and this reads
 * `connectWanted`, so the account-switch strand reads as connecting here,
 * where the ticket (which does not read it) says "not connected" until the
 * watchdog reconnects.
 *
 * Nothing here authorizes anything: it explains the read surfaces only.
 */
import type { AccountStatus } from '@/types/kalshi';

export type KalshiAccountPhase =
  /** Connected; Kalshi rows are current. */
  | 'connected'
  /** Signed in, the key check has not answered yet. */
  | 'checking'
  /** Keys found; connecting or reconnecting. */
  | 'connecting'
  /** The key check itself failed: keys are unknown, not absent. */
  | 'check-failed'
  /** Keys found but the connection failed. */
  | 'unavailable'
  /** The key check answered: no usable keys. */
  | 'absent'
  /** Keys found, not connected, and no connection wanted (a deliberate
   *  disconnect in Settings). */
  | 'not-connected';

export function kalshiAccountPhase(input: {
  status: AccountStatus;
  credentialsStatus: 'unknown' | 'checked' | 'error';
  credentialCount: number;
  /** A connection is wanted (`accountStore.connectWanted`): disconnected with
   *  keys is then a session about to reconnect, not a deliberate disconnect. */
  connectWanted?: boolean;
}): KalshiAccountPhase {
  const { status, credentialsStatus, credentialCount } = input;
  // The order ticket's precedence (useOrderEntryModel's kalshiAccountReason),
  // plus `connectWanted` for disconnected-with-keys (see the header).
  if (status === 'connected') return 'connected';
  if (credentialsStatus === 'error') return 'check-failed';
  if (status === 'connecting') return 'connecting';
  if (status === 'error') return 'unavailable';
  if (credentialsStatus === 'unknown') return 'checking';
  if (credentialCount <= 0) return 'absent';
  return input.connectWanted ? 'connecting' : 'not-connected';
}

/** One short line per phase, as the order ticket words it. */
export const KALSHI_PHASE_TEXT: Record<Exclude<KalshiAccountPhase, 'connected'>, string> = {
  checking: 'Checking Kalshi account…',
  connecting: 'Connecting to Kalshi…',
  'check-failed': "Couldn't check Kalshi keys.",
  unavailable: 'Kalshi account unavailable.',
  absent: 'Kalshi not connected — connect in Settings.',
  'not-connected': 'Kalshi not connected — connect in Settings.',
};
